import { useEffect, useState } from "react";
import styled from "styled-components";
import { Icon } from "../Icon";
import { getAvailablePages } from "./pagination.utils";
import { Page } from "./components/Page";

const INITIAL_PAGE = 1;
const DEFAULT_SIDE_PAGES = 4;

const PaginationContainer = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionWrapper = styled.button`
  border: none;
  background: none;
  width: 24px;
  height: 24px;
  padding: 0;
  cursor: ${({ disabled }) => (!disabled ? "pointer" : "")};
`;

export enum PaginationTestIds {
  Container = "pagination",
  Page = "page",
  PageSpread = "page-spread",
  ChevronLeftPage = "chevron-left-page",
  ChevronRightPage = "chevron-right-page",
}

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  sidePages?: number;
  id?: string;
  testId?: string;
  onChangePage?: (page: number) => void;
};

export const Pagination = ({
  currentPage,
  totalPages,
  sidePages = DEFAULT_SIDE_PAGES,
  testId = PaginationTestIds.Container,
  id,
  onChangePage = () => {},
}: PaginationProps) => {
  const [page, setPage] = useState(currentPage);
  const isFirstPage = page === 1;
  const isLastPage = page === totalPages;
  const validSidePages = sidePages < 1 ? DEFAULT_SIDE_PAGES : sidePages;
  const availablePages = getAvailablePages(validSidePages, page, totalPages);

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  const handleOnChangePage = (selectedPage: number) => {
    if (selectedPage >= INITIAL_PAGE && selectedPage <= totalPages) {
      setPage(selectedPage);
      onChangePage(selectedPage);
    }
  };

  return (
    <PaginationContainer data-testid={testId} id={id}>
      <ActionWrapper
        onClick={() => handleOnChangePage(page - 1)}
        data-testid={PaginationTestIds.ChevronLeftPage}
        aria-label="Previous page"
        disabled={isFirstPage}
      >
        <Icon
          iconName="chevron-left"
          iconSize="regular"
          plain
          variant={isFirstPage ? "DISABLED" : "ACTIVE"}
        />
      </ActionWrapper>
      {availablePages.map((availablePage, index) => (
        <Page
          key={index}
          page={availablePage}
          currentPage={page}
          handleOnChangePage={handleOnChangePage}
          testid={PaginationTestIds.Page}
        />
      ))}
      <ActionWrapper
        onClick={() => handleOnChangePage(page + 1)}
        data-testid={PaginationTestIds.ChevronRightPage}
        aria-label="Next page"
        disabled={isLastPage}
      >
        <Icon
          iconName="chevron-right"
          iconSize="regular"
          plain
          variant={isLastPage ? "DISABLED" : "ACTIVE"}
        />
      </ActionWrapper>
    </PaginationContainer>
  );
};
